import { saveAs } from 'file-saver';

/**
 * Interface for API error responses
 */
interface ApiErrorResponse {
  error: string;
  details?: string;
}

/**
 * Configuration for the PDF conversion service
 */
interface ConversionConfig {
  apiBaseUrl?: string;
  timeout?: number;
}

/**
 * Default configuration
 */
const defaultConfig: ConversionConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001',
  timeout: 120000, // 2 minutes timeout for large documents
};

/**
 * Custom error class for PDF conversion errors
 */
export class PdfConversionError extends Error {
  public details?: string;
  public status?: number;

  constructor(message: string, details?: string, status?: number) {
    super(message);
    this.name = 'PdfConversionError';
    this.details = details;
    this.status = status;
  }
}

/**
 * Check if LibreOffice is available on the server
 * @param config Optional configuration
 * @returns Promise<boolean> - True if LibreOffice is available
 */
export const checkLibreOfficeAvailability = async (
  config: ConversionConfig = {}
): Promise<boolean> => {
  const { apiBaseUrl } = { ...defaultConfig, ...config };

  try {
    const response = await fetch(`${apiBaseUrl}/document/health`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ LibreOffice health check:', result);
      return result.status === 'ok';
    } else {
      console.warn('⚠️ LibreOffice health check failed:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ LibreOffice health check error:', error);
    return false;
  }
};

/**
 * Check if PDF title page and template are available on the server
 * @param config Optional configuration
 * @returns Promise<object> - Template and title page availability info
 */
export const checkTemplateAvailability = async (
  config: ConversionConfig = {}
): Promise<any> => {
  const { apiBaseUrl } = { ...defaultConfig, ...config };

  try {
    const response = await fetch(`${apiBaseUrl}/document/template-info`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ PDF template and title page check:', result);
      return result;
    } else {
      const error = await response.json().catch(() => ({}));
      console.warn('⚠️ PDF template and title page check failed:', error);
      return {
        status: 'not-found',
        message: 'Template and title page not available',
        features: {
          titlePageSupport: false,
          templateOverlaySupport: false,
        },
      };
    }
  } catch (error) {
    console.error('❌ PDF template and title page check error:', error);
    return {
      status: 'error',
      message: 'Failed to check template and title page',
      features: {
        titlePageSupport: false,
        templateOverlaySupport: false,
      },
    };
  }
};

/**
 * Upload a Word document blob to the backend and receive a PDF
 * @param blob The Word document blob (.doc or .docx)
 * @param filename The original filename for the document
 * @param config Optional configuration
 * @param skipTemplateOverlay Optional flag to skip template overlay (for documents that already have letterhead)
 * @returns Promise<void> - Downloads the PDF automatically
 */
export const uploadDocAndGetPdf = async (
  blob: Blob,
  filename: string,
  config: ConversionConfig = {},
  skipTemplateOverlay: boolean = false
): Promise<void> => {
  const { apiBaseUrl, timeout } = { ...defaultConfig, ...config };

  try {
    console.log(
      `📤 Uploading document for PDF conversion: ${filename} (${blob.size} bytes)`
    );

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('file', blob, filename);
    formData.append('skipTemplateOverlay', skipTemplateOverlay.toString());

    // Create AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    // Send the document to the backend
    const response = await fetch(`${apiBaseUrl}/document/convert-to-pdf`, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    // Clear the timeout
    clearTimeout(timeoutId);

    // Check if the request was successful
    if (!response.ok) {
      let errorMessage = `Server returned ${response.status}: ${response.statusText}`;
      let errorDetails: string | undefined;

      try {
        const errorData: ApiErrorResponse = await response.json();
        errorMessage = errorData.error || errorMessage;
        errorDetails = errorData.details;
      } catch (parseError) {
        console.warn('Failed to parse error response as JSON');
      }

      throw new PdfConversionError(errorMessage, errorDetails, response.status);
    }

    // Check if the response is actually a PDF
    const contentType = response.headers.get('Content-Type');
    if (!contentType || !contentType.includes('application/pdf')) {
      throw new PdfConversionError(
        'Server did not return a PDF file',
        `Received content type: ${contentType}`
      );
    }

    // Get the PDF blob
    const pdfBlob = await response.blob();

    if (pdfBlob.size === 0) {
      throw new PdfConversionError('Received empty PDF file');
    }

    // Generate download filename
    const baseName = filename.replace(/\.(doc|docx)$/i, '');
    const pdfFilename = `${baseName}.pdf`;

    console.log(`📥 Downloading PDF: ${pdfFilename} (${pdfBlob.size} bytes)`);

    // Automatically download the PDF using FileSaver
    saveAs(pdfBlob, pdfFilename);

    console.log('✅ PDF conversion and download completed successfully');
  } catch (error) {
    console.error('❌ PDF conversion failed:', error);

    if (error instanceof PdfConversionError) {
      throw error;
    }

    if ((error as Error).name === 'AbortError') {
      throw new PdfConversionError(
        'PDF conversion timed out',
        `Operation exceeded ${timeout! / 1000} seconds`
      );
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new PdfConversionError(
        'Network error during PDF conversion',
        'Please check your internet connection and try again'
      );
    }

    // Generic error fallback
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new PdfConversionError(
      'Unexpected error during PDF conversion',
      errorMessage
    );
  }
};

/**
 * Create a Word document blob from HTML content using the existing wordExport utility
 * @param title Document title
 * @param htmlContent HTML content to export
 * @param tocContent Optional TOC markdown content
 * @param properties Additional document properties
 * @param styleOptions Custom styling options
 * @returns Promise<Blob> - Word document as blob
 */
export const createWordDocumentBlob = async (
  title: string,
  htmlContent: string,
  tocContent?: string,
  properties?: any,
  styleOptions?: any
): Promise<Blob> => {
  // Import the existing word export utilities
  const { exportToWord } = await import('./wordExport');

  // We need to modify the existing exportToWord to return a blob instead of downloading
  // For now, we'll create a simplified version
  const docProperties = {
    title: title || 'Document',
    subtitle: 'Exported from KI-Agent',
    ...properties,
  };

  // Create a basic Word-compatible HTML document
  const htmlDocContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${docProperties.title}</title>
      <style>
        /* Custom Montserrat font */
        @font-face {
          font-family: 'Montserrat';
          src: url('/public/fonts/Montserrat-Regular.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        
        @font-face {
          font-family: 'Montserrat';
          src: url('/public/fonts/Montserrat-Bold.ttf') format('truetype');
          font-weight: bold;
          font-style: normal;
        }

        body { 
          font-family: 'Montserrat', Arial, sans-serif !important; 
          font-size: 12pt !important; 
          line-height: 1.5 !important; 
          margin: 0mm !important; 
          padding: 0mm !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        h1, h2, h3 { 
          font-family: 'Montserrat', Arial, sans-serif !important;
          color: #ee8a81 !important; 
          font-weight: bold !important; 
        }
        h1 { 
          font-size: 18pt !important; 
          border-bottom: 1pt solid #ee8a81 !important; 
          padding-bottom: 6pt !important; 
        }
        h2 { font-size: 16pt !important; }
        h3 { font-size: 14pt !important; }
        p { 
          margin-bottom: 8pt !important; 
          margin-left: 0pt !important;
          margin-right: 0pt !important;
          padding-left: 0pt !important;
          padding-right: 0pt !important;
          text-align: justify !important; 
          width: 100% !important;
        }
        ul, ol { 
          margin-top: 6pt !important; 
          margin-bottom: 8pt !important; 
          margin-left: 0pt !important;
          margin-right: 0pt !important;
          padding-left: 15pt !important; 
          padding-right: 0pt !important;
          width: 100% !important;
        }
        li { margin-bottom: 4pt !important; }
        .cover-page { text-align: center !important; padding-top: 100pt !important; page-break-after: always !important; }
        .doc-title { font-size: 24pt !important; font-weight: bold !important; margin-bottom: 20pt !important; text-transform: uppercase !important; }
        .doc-category { font-size: 16pt !important; margin-bottom: 10pt !important; }
        .page-break { page-break-after: always !important; }
        
        /* TOC Specific Styles */
        .toc-main { 
          padding: 10pt 0 !important; 
          font-size: 13pt !important; 
          font-weight: bold !important; 
          color: #222 !important;
          margin-left: 0pt !important;
        }
        .toc-sub { 
          padding: 8pt 0 8pt 20pt !important; 
          font-size: 12pt !important; 
          font-weight: normal !important; 
          color: #333 !important;
          margin-left: 20pt !important;
        }
        .toc-sub-sub { 
          padding: 6pt 0 6pt 40pt !important; 
          font-size: 11.5pt !important; 
          font-weight: normal !important; 
          color: #444 !important;
          margin-left: 40pt !important;
        }
        .toc-deep { 
          padding: 4pt 0 4pt 60pt !important; 
          font-size: 11pt !important; 
          font-weight: normal !important; 
          color: #555 !important;
          margin-left: 60pt !important;
        }
        .toc-number {
          font-weight: inherit !important;
          margin-right: 10pt !important;
          display: inline-block !important;
        }
        .toc-container {
          margin-left: 0pt !important;
          margin-right: 0pt !important;
          line-height: 1.4 !important;
        }
        
        /* Remove all possible container margins */
        div, section, article, main, .document-content {
          margin-left: 0pt !important;
          margin-right: 0pt !important;
          padding-left: 2pt !important;
          padding-right: 2pt !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        
        /* Ensure cover page uses full width */
        .cover-page {
          margin-left: 0pt !important;
          margin-right: 0pt !important;
          padding-left: 0pt !important;
          padding-right: 0pt !important;
        }

     
          
      </style>
    </head>
    <body>
    
      
      ${
        tocContent
          ? `
          <div class="toc-section">
            <h2>Inhaltsverzeichnis</h2>
            <div class="toc-container">
              ${processTocForWord(tocContent)}
            </div>
          </div>
         
      `
          : ''
      }
      
      <div class="document-content" style="margin: 0 !important; padding: 2pt !important; width: 100% !important;">
        <p style="page-break-before: always;"></p>
        ${htmlContent}
      </div>
    </body>
    </html>
  `;

  return new Blob([htmlDocContent], { type: 'application/msword' });
};

/**
 * Process TOC content for Word document with proper nested levels
 */
const processTocForWord = (tocContent: string): string => {
  if (!tocContent) return '';

  try {
    const lines = tocContent.split('\n');
    let tocHtml = '';

    // Regular expressions to match different levels of TOC entries
    const mainSectionRegex = /^(\d+)\.?\s+(.+)$/;
    const subSectionRegex = /^\s*-?\s*(\d+)\.(\d+)\s+(.+)$/;
    const subSubSectionRegex = /^\s*-?\s*(\d+)\.(\d+)\.(\d+)\s+(.+)$/;
    const deepSubSectionRegex = /^\s*-?\s*(\d+)\.(\d+)\.(\d+)\.(\d+)\s+(.+)$/;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Skip empty lines and heading lines (# or ##)
      if (!line || line.startsWith('#')) continue;

      // Check for deep sub-section (e.g., 6.7.1.1 Detailed item)
      const deepSubMatch = line.match(deepSubSectionRegex);
      if (deepSubMatch) {
        const mainNum = deepSubMatch[1];
        const subNum = deepSubMatch[2];
        const subSubNum = deepSubMatch[3];
        const deepNum = deepSubMatch[4];
        const content = deepSubMatch[5].trim();

        tocHtml += `<div class="toc-deep">
           <span class="toc-number">${mainNum}.${subNum}.${subSubNum}.${deepNum}</span>${content}
         </div>\n`;
        continue;
      }

      // Check for sub-sub-section (e.g., 6.7.1 Das Betreuerbezugssystem)
      const subSubMatch = line.match(subSubSectionRegex);
      if (subSubMatch) {
        const mainNum = subSubMatch[1];
        const subNum = subSubMatch[2];
        const subSubNum = subSubMatch[3];
        const content = subSubMatch[4].trim();

        tocHtml += `<div class="toc-sub-sub">
           <span class="toc-number">${mainNum}.${subNum}.${subSubNum}  </span>${content}
         </div>\n`;
        continue;
      }

      // Check for sub-section (e.g., 5.1 Rechtliche Grundlagen der Hilfe)
      const subMatch = line.match(subSectionRegex);
      if (subMatch) {
        const mainNum = subMatch[1];
        const subNum = subMatch[2];
        const content = subMatch[3].trim();

        tocHtml += `<div class="toc-sub">
           <span class="toc-number">${mainNum}.${subNum}  </span>${content}
         </div>\n`;
        continue;
      }

      // Check for main section (e.g., 1. Unser Leitbild)
      const mainMatch = line.match(mainSectionRegex);
      if (mainMatch) {
        const number = mainMatch[1];
        const content = mainMatch[2].trim();

        tocHtml += `<div class="toc-main">
           <span class="toc-number">${number}.  </span>${content}
         </div>\n`;
      }
    }

    return tocHtml;
  } catch (error) {
    console.error('Error processing TOC content:', error);
    return '';
  }
};

/**
 * Higher-level function that combines Word export and PDF conversion
 * @param title Document title
 * @param htmlContent HTML content to export
 * @param tocContent Optional TOC markdown content
 * @param properties Additional document properties
 * @param styleOptions Custom styling options
 * @param config Optional conversion configuration
 */
export const exportHtmlToPdfViaWord = async (
  title: string,
  htmlContent: string,
  tocContent?: string,
  properties?: any,
  styleOptions?: any,
  config: ConversionConfig = {}
): Promise<void> => {
  try {
    console.log('📝 Creating Word document for PDF conversion...');

    // Create a Word document blob
    const wordBlob = await createWordDocumentBlob(
      title,
      htmlContent,
      tocContent,
      properties,
      styleOptions
    );

    console.log('🔄 Converting Word document to PDF via backend...');

    // Upload and convert to PDF with title in filename
    const filename = `${title.replace(/[^a-z0-9\s]/gi, '_').trim()}.doc`;
    await uploadDocAndGetPdf(wordBlob, filename, config);
  } catch (error) {
    console.error('❌ HTML to PDF via Word conversion failed:', error);
    throw error;
  }
};
